import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import {
  ShipmentType,
  ShipmentStatus,
  CrossDockStatus,
  ShipmentLoadType,
  Incoterm,
  ShipmentServiceType,
  ShipmentTrackingStatus,
} from "../util/enums";

export interface ContainerSizeOptions extends Option {
  lengthSize: number;
  width: number;
  height: number;
}

const getShipmentOptions = (isFmVersion = true) => {
  const { t: translate } = useTranslation();

  const typeOptions: Option[] = [
    { value: ShipmentType.AIR, label: translate("air") },
    { value: ShipmentType.RAIL, label: translate("rail") },
    { value: ShipmentType.ROAD, label: translate("road") },
    { value: ShipmentType.SEA, label: translate("sea") },
  ];

  const statusOptions: Option[] = [
    { value: ShipmentStatus.PLANNED, label: "Planned" },
    { value: ShipmentStatus.BOOKED, label: "Booked" },
    { value: ShipmentStatus.STAGED, label: "Staged" },
    { value: ShipmentStatus.LOADED, label: "Loaded" },
    { value: ShipmentStatus.IN_TRANSIT, label: "In Transit" },
    { value: ShipmentStatus.DELIVERED, label: "Delivered" },
    { value: ShipmentStatus.CANCELLED, label: "Cancelled" },
    { value: ShipmentStatus.SHIPPED, label: "Shipped" },
  ];

  const seaStatusOptions: Option[] = [
    { value: ShipmentStatus.BOOKED, label: "Booked" },
    { value: ShipmentStatus.CANCELLED, label: "Cancelled" },
    { value: ShipmentStatus.SHIPPED, label: "Shipped" },
    { value: ShipmentTrackingStatus.COLLECTED_FROM_SHIPPER, label: "Collected from shipper" },
    {
      value: ShipmentTrackingStatus.NOT_ARRIVED_AT_PORT_OF_LOAD,
      label: "Not arrived at port of load",
    },
    { value: ShipmentTrackingStatus.WAITING_AT_PORT_OF_LOAD, label: "Waiting at port of load" },
    { value: ShipmentTrackingStatus.SHIPPED_ON_BOARD, label: "Shipped on board" },
    { value: ShipmentTrackingStatus.UNDERWAY, label: "Underway" },
    {
      value: ShipmentTrackingStatus.UNDERWAY_TO_A_TRANSHIPMENT_PORT,
      label: "Underway to a transhipment port",
    },
    {
      value: ShipmentTrackingStatus.ARRIVAL_AT_TRANSHIPMENT_PORT,
      label: "Arrival at transhipment port",
    },
    {
      value: ShipmentTrackingStatus.WAITING_AT_TRANSHIPMENT_PORT,
      label: "Waiting at transhipment port",
    },
    { value: ShipmentTrackingStatus.LEFT_THE_DESTINATION_PORT, label: "Left the destination port" },
    { value: ShipmentTrackingStatus.SHIPMENT_COMPLETED, label: "Shipment completed" },
    { value: ShipmentTrackingStatus.WAITING_FOR_UPDATE, label: "Waiting for update" },
    { value: ShipmentTrackingStatus.TRACKING_NOT_AVAILABLE, label: "Tracking not available" },
    { value: ShipmentStatus.COMPLETED, label: "Completed" },
  ];

  // lengthSize, width and height are in meters
  const containerTypeOptions: ContainerSizeOptions[] = [
    {
      value: "20_dry_standard",
      label: `20" ${translate("dryStandard")}`,
      lengthSize: 5.9,
      width: 2.352,
      height: 2.393,
    },
    {
      value: "40_dry_standard",
      label: `40" ${translate("dryStandard")}`,
      lengthSize: 12.034,
      width: 2.352,
      height: 2.395,
    },
    {
      value: "40_dry_high",
      label: `40" ${translate("dryHigh")}`,
      lengthSize: 12.034,
      width: 2.352,
      height: 2.7,
    },
    {
      value: "45_dry_high",
      label: `45" ${translate("dryHigh")}`,
      lengthSize: 13.556,
      width: 2.352,
      height: 2.7,
    },
    {
      value: "20_tank",
      label: `20" ${translate("tank")}`,
      lengthSize: 6.058,
      width: 2.438,
      height: 2.591,
    },
    {
      value: "40_tank",
      label: `40" ${translate("tank")}`,
      lengthSize: 6.058,
      width: 2.438,
      height: 2.591,
    },
    {
      value: "20_open_top",
      label: `20" ${translate("openTop")}`,
      lengthSize: 5.898,
      width: 2.352,
      height: 2.348,
    },
    {
      value: "40_open_top",
      label: `40" ${translate("openTop")}`,
      lengthSize: 12.032,
      width: 2.352,
      height: 2.348,
    },
    {
      value: "40_open_top_high",
      label: `40" ${translate("openTopHigh")}`,
      lengthSize: 12.032,
      width: 2.352,
      height: 2.348,
    },
    {
      value: "40_flat_standard",
      label: `40" ${translate("flatStandard")}`,
      lengthSize: 12.054,
      width: 2.44,
      height: 1.959,
    },
    {
      value: "40_flat_high",
      label: `40" ${translate("flatHigh")}`,
      lengthSize: 12.054,
      width: 2.44,
      height: 1.959,
    },
    {
      value: "20_flat",
      label: `20" ${translate("flat")}`,
      lengthSize: 5.92,
      width: 2.44,
      height: 2.213,
    },
    {
      value: "20_reefer_standard",
      label: `20" ${translate("reeferStandard")}`,
      lengthSize: 5.456,
      width: 2.294,
      height: 2.273,
    },
    {
      value: "40_reefer_high",
      label: `40" ${translate("reeferHigh")}`,
      lengthSize: 11.584,
      width: 2.294,
      height: 2.557,
    },
  ];

  const containerTypeOptionsOnlyForSeaAndFCLLoadType: ContainerSizeOptions[] = [
    ...containerTypeOptions,
    {
      value: "d-container",
      label: translate("dContainer"),
      lengthSize: 1.47,
      width: 1.067,
      height: 1.14,
    }, // D-Container 58″	42″	45″
    {
      value: "e-container",
      label: translate("eContainer"),
      lengthSize: 1.067,
      width: 0.74,
      height: 0.65,
    }, // E-Container 42″	29″	25 ½”
    {
      value: "ldn_container",
      label: translate("ldnContainer"),
      lengthSize: 1.42,
      width: 1.4,
      height: 1.45,
    }, // LDN Container, 56″	55″	57″
    {
      value: "liftvan",
      label: `${translate("liftvan")} 87"x87"x48"`,
      lengthSize: 2.21,
      width: 2.21,
      height: 1.22,
    }, // LDN Container,
    {
      value: "eur1_iso1_pallet",
      label: `${translate("eur1Iso1Pallet")} (80x120cm)`,
      lengthSize: 0.8,
      width: 1.2,
      height: 1,
    }, // LDN Container,
    {
      value: "eur2_iso2_pallet",
      label: `${translate("eur2Iso2Pallet")} (120x100cm)`,
      lengthSize: 1.2,
      width: 1,
      height: 1,
    }, // LDN Container,
    {
      value: "pallet",
      label: `48"x40" ${translate("pallet")}`,
      lengthSize: 1.22,
      width: 1.016,
      height: 1,
    }, // LDN Container,
  ];

  const crossDcokStatusOption: Option[] = [
    { value: CrossDockStatus.RECEIVED_EXPECTED, label: translate("receivedExpected") },
    { value: CrossDockStatus.RECEIVED_UNEXPECTED, label: translate("receivedUnexpected") },
    { value: CrossDockStatus.NOT_RECEIVED, label: translate("notReceivedExpected") },
  ];

  const serviceTypeOptions: Option[] = [
    { value: ShipmentServiceType.REGULAR_CARGO, label: translate("regularCargo") },
    { value: ShipmentServiceType.HAZARDOUS_CARGO, label: translate("hazardousCargo") },
    { value: ShipmentServiceType.FOOD_GRADE_CARGO, label: translate("foodGradeCargo") },
    { value: ShipmentServiceType.RARE_CARGO, label: translate("rareCargo") },
    { value: ShipmentServiceType.REEFER_CARGO, label: translate("reeferCargoRefrigerated") },
  ];

  const shipTermsOptions: Option[] = isFmVersion
    ? [
        { value: "CFS/CY", label: "CFS/CY" },
        { value: "CY/CY", label: "CY/CY" },
        { value: " CFS/CFS", label: " CFS/CFS" },
      ]
    : [
        { value: "CY/CY", label: "CY/CY" },
        { value: "CFS/CY", label: "CFS/CY" },
      ];

  const deliverToOptions: Option[] = [
    { value: "port", label: translate("port") },
    { value: "cfs", label: translate("cfs") },
  ];

  const sizeOptions: Option[] = [
    { value: "20DRY/20REEFER/20HNOR", label: "20DRY/20REEFER/20HNOR" },
    { value: "40DRY", label: "40DRY" },
    { value: "40HIGH", label: "40HIGH" },
    { value: "40REEFER/40HNOR", label: "40REEFER/40HNOR" },
    { value: "45HIGH", label: "45HIGH" },
  ];

  const boundOptions: Option[] = [
    { value: "Inbound", label: "Inbound" },
    { value: "Outbound", label: "Outbound" },
  ];

  const airLoadTypeOptions: Option[] = [
    { value: ShipmentLoadType.BOX, label: translate("box") },
    { value: ShipmentLoadType.CASE, label: translate("case") },
    { value: ShipmentLoadType.PALLET, label: translate("pallet") },
  ];

  const roadLoadTypeOptions: Option[] = [
    { value: ShipmentLoadType.FTL, label: translate("ftl") },
    { value: ShipmentLoadType.LTL, label: translate("ltl") },
    { value: ShipmentLoadType.GROUPAGE_VANLINE, label: translate("groupageVanline") },
  ];

  const seaLoadTypeOptions: Option[] = [
    { value: ShipmentLoadType.FCL, label: translate("fcl") },
    { value: ShipmentLoadType.LCL, label: translate("lcl") },
    { value: ShipmentLoadType.GROUPAGE, label: translate("groupage") },
    { value: ShipmentLoadType.RORO, label: translate("roro") },
    { value: ShipmentLoadType.BULK, label: translate("bulk") },
  ];

  const railLoadTypeOptions: Option[] = [];

  const incotermOptions: Option[] = [
    { value: Incoterm.CIP, label: Incoterm.CIP },
    { value: Incoterm.CPT, label: Incoterm.CPT },
    { value: Incoterm.DAP, label: Incoterm.DAP },
    { value: Incoterm.DDP, label: Incoterm.DDP },
    { value: Incoterm.DPU, label: Incoterm.DPU },
    { value: Incoterm.EXW, label: Incoterm.EXW },
    { value: Incoterm.FCA, label: Incoterm.FCA },
  ];

  const seaIncotermOptions: Option[] = [
    { value: Incoterm.CFR, label: Incoterm.CFR },
    { value: Incoterm.CIF, label: Incoterm.CIF },
    ...incotermOptions,
    { value: Incoterm.FAS, label: Incoterm.FAS },
    { value: Incoterm.FOB, label: Incoterm.FOB },
  ];

  return {
    typeOptions,
    statusOptions,
    containerTypeOptions,
    crossDcokStatusOption,
    serviceTypeOptions,
    shipTermsOptions,
    deliverToOptions,
    sizeOptions,
    boundOptions,
    airLoadTypeOptions,
    roadLoadTypeOptions,
    seaLoadTypeOptions,
    railLoadTypeOptions,
    incotermOptions,
    seaIncotermOptions,
    seaStatusOptions,
    containerTypeOptionsOnlyForSeaAndFCLLoadType,
  };
};

export default getShipmentOptions;
